import type { Board } from "../board";
import { DamageType } from "../damageType";
import { cycleDirection, type Direction, directionDelta } from "../direction";
import { Timing } from "../effects/effect";
import { Explosion } from "../effects/explosion";
import { Laser } from "../effects/laser";
import { isProjectileHittable } from "../interfaces";
import { PhysicalThingWithHp, STD_BASE_HP } from "../physicalThingWithHp";
import type { Player } from "../player";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { Weapon } from "../weapon";
import type { XY } from "../xy";

const RANGE = 90;

export class OpGun extends Weapon {
	static key = "OpGun";
	constructor(owner: Player) {
		super(owner, 0, true);
	}

	fire(direction: Direction, board: Board): void {
		board.addNonPhysicalThing(new Sound(SoundId.LASER_BEAM));
		for (let cycle = -1; cycle <= 1; cycle++) {
			const startDir = cycleDirection(direction, cycle);
			const startXY = this.owner.xy.add(directionDelta(startDir));
			OpGun.blastLine(board, startXY, direction);
		}
	}

	private static blastLine(board: Board, startXY: XY, direction: Direction): void {
		const delta = directionDelta(direction);
		let current = startXY;

		for (let n = 0; n < RANGE; n++) {
			if (!board.isInBounds(current)) break;

			const laser = new Laser(current, direction);
			laser.setTiming(Timing.BEFORE_STATE_UPDATE);
			board.addEffect(laser);

			const things = board.getThingsAt(current);
			const targets = things.filter(
				(t) => t.active && isProjectileHittable(t) && t.isHittable() && t instanceof PhysicalThingWithHp,
			) as PhysicalThingWithHp[];

			for (const target of targets) {
				const damageDealt = target.applyDamage(DamageType.EXPLOSION, STD_BASE_HP * 10, null);
				if (damageDealt > 0) {
					const explosionBefore = new Explosion(target.xy);
					explosionBefore.setTiming(Timing.BEFORE_STATE_UPDATE);
					board.addEffect(explosionBefore);
					board.addEffect(new Explosion(target.xy));
				}
			}

			current = current.add(delta);
		}
	}

	static deserialize(_data: any, owner: Player): OpGun {
		return new OpGun(owner);
	}
}
